import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { CardSkeletonGrid, EmptyState, ErrorState, Section } from '../../components/common/States'
import { HotelCard } from './HotelCard'
import { contentApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import type { PriceBand } from '../../types/api'

const BANDS: PriceBand[] = ['BUDGET', 'MID', 'UPSCALE', 'LUXURY']

export function HotelsPage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [priceBand, setPriceBand] = useState<PriceBand | undefined>()

  const hotels = useQuery({
    queryKey: ['hotels', locale, priceBand],
    queryFn: () => contentApi.hotels(locale, { destination: 'helsinki', priceBand }),
  })

  return (
    <>
      <PageMeta title={t('hotels.title')} description={t('hotels.subtitle')} path="/hotels" />

      <Section title={t('hotels.title')} subtitle={t('hotels.subtitle')}>
        <Stack direction="row" spacing={1} sx={{ mb: 3.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip
            label={t('common.all')}
            onClick={() => setPriceBand(undefined)}
            color={priceBand === undefined ? 'primary' : 'default'}
            variant={priceBand === undefined ? 'filled' : 'outlined'}
          />
          {BANDS.map((band) => (
            <Chip
              key={band}
              label={t(`hotels.${band}`)}
              onClick={() => setPriceBand(band)}
              color={priceBand === band ? 'primary' : 'default'}
              variant={priceBand === band ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>

        {hotels.isPending && <CardSkeletonGrid count={4} height={280} />}
        {hotels.isError && <ErrorState error={hotels.error} onRetry={() => void hotels.refetch()} />}
        {hotels.data?.length === 0 && <EmptyState icon="🏨" title={t('explore.noResults')} />}
        {hotels.data && hotels.data.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            }}
          >
            {hotels.data.map((hotel) => (
              <HotelCard key={hotel.id} hotel={hotel} />
            ))}
          </Box>
        )}
      </Section>
    </>
  )
}
